// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
The most reliable way to size a ring at home is to measure a ring you already wear, not your finger. Lay it flat, measure the inside edge to edge in millimetres, and multiply by 3.14. That number is your circumference, and in most of Europe it is your ring size as written.

If you have no ring to measure, wrap a strip of paper round the finger and measure that instead. Both methods are below, along with the mistakes that cost people a return.

## Method one: measure a ring that fits

This is the accurate one, because a ring cannot lie about its own size.

1. Pick a ring you wear on the finger in question, with a similar band width to the one you are buying.
2. Lay it flat on a table and hold a millimetre ruler across the inside, edge to edge, through the centre. That is the inside diameter.
3. Multiply by 3.14 to get the inside circumference in millimetres.

A ring measuring 17.3 mm across the inside gives 17.3 x 3.14, which is 54.3 mm. That is a European size 54, a US size 7, or a UK size N and a half.

Measure twice. A quarter of a millimetre of error in the diameter becomes most of a size once you multiply.

## Method two: measure the finger

Use this when there is no ring to copy.

1. Cut a strip of paper about 5 mm wide, or use a strip of non stretchy ribbon. Do not use string, which stretches and twists.
2. Wrap it round the base of the finger, snug but not tight, and mark where it overlaps.
3. Lay it flat and measure to the mark in millimetres. That is your circumference, straight away.

Then check the knuckle. Slide the loop off over the knuckle without loosening it. If it will not pass, your knuckle is the limiting measurement and you need to size up until the loop just slides over, or the ring will never go on.

## The numbers

Circumference in millimetres is the international standard, and it is what the European and Japanese systems are built on. Here are the common conversions.

- **46.8 mm**: US 4, UK H and a half
- **49.3 mm**: US 5, UK J and a half
- **51.9 mm**: US 6, UK L and a half
- **54.4 mm**: US 7, UK N and a half
- **57.0 mm**: US 8, UK P and a half
- **59.5 mm**: US 9, UK R and a half
- **62.1 mm**: US 10, UK T and a half
- **64.6 mm**: US 11, UK V and a half
- **67.2 mm**: US 12, UK X and a half

US sizes go up in quarters and halves between these. Each full US size is roughly 2.5 mm of circumference, so a half size is a little over a millimetre. That is small enough that measuring carefully genuinely matters.

## The five mistakes that cause returns

**Measuring in the morning.** Fingers are at their smallest when you wake and largest in the evening and in heat. Measure at the end of the day, at normal room temperature, and if you get two different numbers on two days, take the larger.

**Measuring cold hands.** Cold fingers can read a full size small. Warm up first.

**Ignoring band width.** A wide band takes up more of the finger and feels tighter than a narrow one at the same size. For anything above about 6 mm wide, go up a quarter to a half size. Antique wedding bands are often wide.

**Forgetting the knuckle.** If your knuckle is much larger than the base of the finger, size to the knuckle and accept that the ring will be a little loose at rest. Sizing beads, a small pair of bumps soldered inside the shank, stop it spinning.

**Using a printed sizer on screen.** Printable ring size charts only work if the page prints at exactly 100 percent, which it usually does not. Check the printed scale against a ruler before trusting it.

## Which finger, and which hand

Your dominant hand is usually a quarter to a half size larger than the other. Size for the finger and the hand the ring will actually be worn on, and say so when you ask a seller a question.

## Antique rings and the resizing problem

This is the part that matters most when buying second hand, and it is where people get caught out.

Some rings cannot be resized at all, or cannot be resized safely.

- **Eternity rings** with stones all the way round have no plain metal to cut into.
- **Rings with hallmarks on the shank** lose the hallmark if the marked section is cut out, and with British assay marks that means losing the date and the assay office record.
- **Carved, engraved or patterned shanks** lose the pattern where the new metal goes in, and matching hand engraving is skilled, slow work.
- **Rings with closed backs or foiled stones**, common in Georgian and early Victorian work, cannot take the heat of soldering without risking the foil and the stone.
- **Tension set and some modern designs** rely on the exact geometry of the band.

Where resizing is possible, going down is generally simpler than going up, and a jeweller will usually take a piece two sizes in either direction without trouble. Beyond that the shank starts to look wrong, and the honest answer is often a new shank.

Two more things worth knowing. Older rings are frequently on their second or third shank already, which is normal and not a defect, but it is worth asking. And every resize is a small loss of originality on an antique, so a ring that already fits is worth paying a little more for.

## Non permanent fixes

If a ring is slightly loose and you would rather not alter it, there are options that change nothing.

- **Ring guards or sizing springs** clip inside the shank and can be removed.
- **Sizing beads** are soldered inside, which is a small, reversible intervention.
- **Silicone inserts** cost almost nothing and work well for occasional wear.

For a ring that is slightly tight, there is no equivalent trick. Do not force it.

## Asking a seller about size

On a marketplace, size is the single most common reason a piece gets sent back, so ask before you buy rather than after.

- Ask for the inside diameter in millimetres, measured with a ruler or callipers. It removes all conversion confusion between US, UK and European systems.
- Ask the band width in millimetres too.
- Ask whether the ring has been resized, and whether it can be.
- If the ring is marked with a size, ask the seller to confirm it against a measurement. Old size stamps are often wrong after a resize.

Every listing on Nextmine is sold with the buyer's payment held by Stripe until the piece has arrived and been confirmed, so if something is genuinely not as described you are not arguing with a stranger about a bank transfer. You can read the mechanics on our [how payments work](/how-payments-work) page, or [browse the rings listed right now](/s).

If you are on the selling side, measuring the inside diameter and putting it in the listing prevents most size questions before they are asked. Our [guide to selling antique jewelry online](/blog/how-to-sell-antique-jewelry-online) covers the rest of what a listing needs.
`;

export default body;
